import moment from "moment";
import { useState, useEffect, useCallback } from "react";
import { DATE_FORMATS } from "../../../services/utils/date-and-time.utils";
import FlexBox from "../../../components/layouts/Flexbox";
import {
  AppAlert,
  AppButton,
  CancelButton,
  ChipList,
  DefaultButton,
  PrimaryButton,
  RequestCoordinationButton,
} from "../../../components/form";
import {
  Box,
  Card,
  Divider,
  Stack,
  Typography,
  alpha,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import { convertToFullName } from "../../../services/utils";
import { APP_COLORS, APP_FONT_FAMILIES } from "../../../enums";
import { FormHelperTextError } from "../../../components/form/errors";
import { useParams, useNavigate } from "react-router-dom";
import { useAppDispatch } from "../../../store";
import { showNotification } from "../../../store/modules/UI";
import { ROUTE_PATHS } from "../../../routes/paths";
import { getBlockedDates } from "../../../api/projects";
import { AddMembersModal } from "../../../components/modal/add-members-modal";
import { BrowserTimezoneAlert } from "../../../components/BrowserTimezoneAlert";

export function AmbassadorSuggestedSlotAlert({
  slots,
  onRejectClick,
  onApproveClick,
  onSubmitSuggestedSlots,
  submitting = false,
  ambassadorDetails,
  availableTimeSlots,
  suggestedTimeSlots,
  additionalMembers = [],
}) {
  const { id } = useParams();
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const theme = useTheme();
  const isNonDesktop = useMediaQuery(theme.breakpoints.down("lg"));
  const defaultSelected =
    slots?.length === 1
      ? moment(slots?.[0]?.from).format(DATE_FORMATS.fullDateWithTime)
      : null;
  const [selectedSlot, setSelectedSlot] = useState(defaultSelected);
  const [selectionError, setSelectionError] = useState("");
  const [blockedDates, setBlockedDates] = useState<string[]>([]);
  const [loadingBlockedDates, setLoadingBlockedDates] = useState(false);


  const initialEmails = additionalMembers?.map((member: any) => member?.email).filter(Boolean) || [];
  const [emailIds, setEmailIds] = useState<{
    list: string[];
    invalidEmailIds: string[];
  }>({
    list: initialEmails,
    invalidEmailIds: [],
  });
  const [soloAttendanceChecked, setSoloAttendanceChecked] = useState(initialEmails.length === 0);
  const [showAddMembersModal, setShowAddMembersModal] = useState(false);
  const [pendingApproval, setPendingApproval] = useState<any>(null);


  const fetchBlockedDates = useCallback(async () => {
    if (!slots || slots.length === 0) return;

    setLoadingBlockedDates(true);
    try {

      const slotDates = slots.map((slot) => moment(slot?.from));
      const earliestDate = moment.min(slotDates);
      const latestDate = moment.max(slotDates);

      const from = earliestDate.startOf("month").format("YYYY-MM-DD");
      const to = latestDate.endOf("month").format("YYYY-MM-DD");

      const response = await dispatch(getBlockedDates({ from, to }));

      if (response?.success && response?.data) {
        setBlockedDates(response.data);
      }
    } catch (error) {

      console.error("Failed to fetch blocked dates:", error);
    } finally {
      setLoadingBlockedDates(false);
    }
  }, [slots, dispatch]);

  useEffect(() => {
    fetchBlockedDates();
  }, [fetchBlockedDates]);


  useEffect(() => {
    if (selectedSlot && blockedDates.length > 0) {
      const slotObj = slots?.find(
        (s) =>
          moment(s?.from).format(DATE_FORMATS.fullDateWithTime) === selectedSlot
      );

      if (slotObj) {
        const dateStr = moment(slotObj?.from).format("YYYY-MM-DD");
        if (blockedDates.includes(dateStr)) {
          setSelectedSlot(null);
        }
      }
    }
  }, [blockedDates, selectedSlot, slots]);


  const isSlotDisabled = useCallback((slotDateString: string) => {
    if (!slotDateString || blockedDates.length === 0) return false;


    const slotObj = slots?.find(
      (s) =>
        moment(s?.from).format(DATE_FORMATS.fullDateWithTime) === slotDateString
    );

    if (!slotObj) return false;


    const dateStr = moment(slotObj?.from).format("YYYY-MM-DD");


    return blockedDates.includes(dateStr);
  }, [blockedDates, slots]);

  const handleSlotsSelection = (slot) => {

    if (isSlotDisabled(slot)) {
      dispatch(
        showNotification({
          message: "This time is already full. Please select a different time.",
          type: "error",
        })
      );
      return;
    }
    setSelectedSlot(slot);
  };

  const validateAttendeeRequirement = (): boolean => {

    const hasValidEmails = emailIds.list.length > 0 && emailIds.invalidEmailIds.length === 0;
    const hasSoloAttendance = soloAttendanceChecked;

    return hasValidEmails || hasSoloAttendance;
  };

  const handleAddMembersSubmit = (data: { emails: string[]; soloAttendance: boolean }) => {
    setEmailIds({
      list: data.emails,
      invalidEmailIds: [],
    });
    setSoloAttendanceChecked(data.soloAttendance);
    setShowAddMembersModal(false);


    if (pendingApproval) {
      proceedWithApproval(pendingApproval);
      setPendingApproval(null);
    }
  };

  const proceedWithApproval = (selectedSlotObj: any) => {
    const payload: any = {
      selectedTimeSlot: selectedSlotObj,
      isCustom: false,
    };


    if (emailIds.list.length > 0) {
      payload.additionalMembers = emailIds.list.map((email) => ({ email }));
    }

    onApproveClick(payload);
  };

  const handleOnApproveClick = () => {
    if (!selectedSlot) {
      setSelectionError("*Please select a time to proceed.");
      return;
    }


    if (isSlotDisabled(selectedSlot)) {
      dispatch(
        showNotification({
          message: "This time is already full. Please select a different time.",
          type: "error",
        })
      );
      return;
    }

    const selectedSlotObj = slots.find(
      (s) =>
        moment(s?.from).format(DATE_FORMATS.fullDateWithTime) === selectedSlot
    );

    if (!selectedSlotObj) {
      dispatch(
        showNotification({
          message: "Please select a time to approve",
          type: "error",
        })
      );
      return;
    }


    if (!validateAttendeeRequirement()) {

      setPendingApproval(selectedSlotObj);
      setShowAddMembersModal(true);
      return;
    }


    proceedWithApproval(selectedSlotObj);
  };

  const onSuggestSlotsClick = () => {
    if (onSubmitSuggestedSlots) {
      onSubmitSuggestedSlots();
    }
  };

  const onRequestCoordinationSuccess = () => {
    navigate(ROUTE_PATHS.PROJECT.LIST);
  };

  const shouldShowHelpButton =
    availableTimeSlots &&
    availableTimeSlots?.length > 0 &&
    suggestedTimeSlots &&
    suggestedTimeSlots?.length > 0;


  const hasDisabledSlots = slots?.some((slot) => {
    if (!slot?.from || blockedDates.length === 0) return false;
    const dateStr = moment(slot?.from).format("YYYY-MM-DD");
    return blockedDates.includes(dateStr);
  });

  return (
    <FlexBox flexDirection={"column"}>
      <Card sx={{ p: 2, bgcolor: APP_COLORS.yellowLight, border: 0 }} elevation={0}>
        <FlexBox
          justifyContent="space-between"
          alignItems="flex-start"
          gap={2}
          flexWrap="wrap"
        >
          <Typography fontSize={15} mb={1} flex={1} minWidth={220} color={APP_COLORS.yellowDark3}>
            <b>
              {convertToFullName(ambassadorDetails)} @{" "}
              {ambassadorDetails?.company}
            </b>{" "}
            has proposed alternative times for the meeting. Please select a time
            that works for you. For any assistance, email{" "}
            <Typography
              sx={{ color: APP_COLORS.yellowDark3, textDecoration: "underline" }}
              component={"a"}
              fontWeight={600}
              href="mailto:vicky.tuneva@cyberriskalliance.com"
            >
              vicky.tuneva@cyberriskalliance.com
            </Typography>{" "}
          </Typography>
        </FlexBox>

        <Box
          p={2}
          component={Card}
          sx={{ border: 0, borderRadius: 2 }}
          elevation={0}
        >
          <FlexBox
            alignItems="center"
            justifyContent="space-between"
            gap={{ xs: 0, lg: 1.5 }}
            flexWrap="wrap"
            mb={{ xs: 0, lg: 1 }}
          >
            <Typography fontSize={15} fontWeight={600}>
              Please select a time from the list below:
            </Typography>
            {shouldShowHelpButton ? (
              <RequestCoordinationButton
                invitationId={id}
                initiatedBy="vendor"
                size="small"
                variant="outlined"
                color="secondary"
                onSuccess={onRequestCoordinationSuccess}
              />
            ) : (
              <Box />
            )}
          </FlexBox>

          <Box my={1}>
            <BrowserTimezoneAlert />
          </Box>

          <ChipList
            chipProps={{
              size: "small",
              variant: "outlined",
              sx: {
                backgroundColor: "white",
                border: `1px solid ${APP_COLORS.textLight}`,
                cursor: "pointer",
                borderRadius: 2,
                height: 32,
              },
            }}
            selectedChipProps={{
              sx: {
                backgroundColor: APP_COLORS.primary,
                color: "white",
                border: `1px solid ${APP_COLORS.primary}`,
              },
            }}
            onChipClick={handleSlotsSelection}
            value={selectedSlot}
            list={slots?.map((t) =>
              moment(t?.from).format(DATE_FORMATS.fullDateWithTime)
            )}
            flatList
            isChipDisabled={isSlotDisabled}
            direction={isNonDesktop ? "column" : "row"}
          />

          {selectionError && !selectedSlot && (
            <FormHelperTextError
              error={selectionError}
              sx={{ fontFamily: APP_FONT_FAMILIES.manrope, mb: 2 }}
            />
          )}
          {hasDisabledSlots && (
            <Box
              sx={{
                mt: 0.75,
                px: 0.75,
                py: 0.5,
                display: "flex",
                alignItems: "center",
                gap: 0.75,
              }}
            >
              <Box
                sx={{
                  width: 12,
                  height: 12,
                  borderRadius: "2px",
                  backgroundColor: alpha(theme.palette.error.main, 0.25),
                  border: `1px solid ${theme.palette.error.main}`,
                  flexShrink: 0,
                }}
              />
              <Typography
                variant="caption"
                sx={{
                  color: theme.palette.error.main,
                  fontSize: "0.7rem",
                  lineHeight: 1.2,
                }}
              >
                Times are already full for dates marked in red
              </Typography>
            </Box>
          )}

          {validateAttendeeRequirement() && (
            <Box sx={{ mt: 2, mb: 1, p: 2, bgcolor: "grey.50", borderRadius: 1 }}>
              <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", mb: 1 }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                  Attendees
                </Typography>
                <AppButton
                  variant="text"
                  size="small"
                  onClick={() => setShowAddMembersModal(true)}
                  sx={{
                    minWidth: "auto",
                    textTransform: "none",
                    color: "primary.main",
                    "&:hover": {
                      backgroundColor: "transparent",
                    }
                  }}
                >
                  Change Attendees
                </AppButton>
              </Box>
              {soloAttendanceChecked ? (
                <Typography variant="body2" sx={{ color: "text.secondary" }}>
                  ✓ No additional representatives will join — you will attend the meeting yourself.
                </Typography>
              ) : emailIds.list.length > 0 ? (
                <Typography variant="body2" sx={{ color: "text.secondary" }}>
                  ✓ {emailIds.list.length} attendee{emailIds.list.length > 1 ? "s" : ""} added:{" "}
                  {emailIds.list.map((email, index) => (
                    <span key={email}>
                      <Typography
                        component="span"
                        variant="body2"
                        sx={{
                          color: "primary.main",
                          textDecoration: "underline",
                          textDecorationColor: "primary.main",
                        }}
                      >
                        {email}
                      </Typography>
                      {index < emailIds.list.length - 1 && ", "}
                    </span>
                  ))}
                </Typography>
              ) : null}
            </Box>
          )}

          <Divider sx={{ my: 2, borderColor: APP_COLORS.grey }} />


          <Box mt={2}>
            <AppAlert
              severity="info"
              sx={{ fontSize: "small" }}
            >
              If none of these times work for you, you can suggest alternative times by clicking the &quot;Suggest alternatives&quot; button below.
            </AppAlert>
          </Box>
          {isNonDesktop ? (
            <Stack gap={1.5} width="100%" sx={{ py: 1.5 }}>
              <DefaultButton
                fullWidth
                onClick={onSuggestSlotsClick}
                startIcon={<AddIcon fontSize="small" />}
              >
                Suggest alternatives
              </DefaultButton>
              <PrimaryButton
                fullWidth
                onClick={handleOnApproveClick}
                disabled={submitting}
              >
                {submitting ? "Please wait..." : "Confirm slot"}
              </PrimaryButton>
            </Stack>
          ) : (
            <FlexBox py={1.5} justifyContent="space-between" alignItems="center">
              <DefaultButton
                onClick={onSuggestSlotsClick}
                startIcon={<AddIcon fontSize="small" />}
              >
                Suggest alternatives
              </DefaultButton>
              <PrimaryButton
                onClick={handleOnApproveClick}
                disabled={submitting}
              >
                {submitting ? "Please wait..." : "Confirm slot"}
              </PrimaryButton>
            </FlexBox>
          )}
        </Box>
      </Card>

      <AddMembersModal
        open={showAddMembersModal}
        onClose={() => {
          setShowAddMembersModal(false);
          setPendingApproval(null);
        }}
        onSubmit={handleAddMembersSubmit}
        initialEmails={emailIds.list}
        initialSoloAttendance={soloAttendanceChecked}
        title="Add Attendees"
        submitButtonText="Continue"
        cancelButtonText="Cancel"
      />
    </FlexBox>
  );
}
